import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { GaugeIcon, ShieldCheckIcon, XIcon, ZapIcon } from 'lucide-react';
import { QuickScoringForm } from './QuickScoringForm';

/** Hash que abre el panel desde cualquier página. Link para compartir: https://tu-sitio/#scoring-rapido */
export const SCORING_HASH = '#scoring-rapido';
const ease = [0.23, 1, 0.32, 1] as const;
export function QuickScoringPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const open = location.hash === SCORING_HASH;
  const close = useCallback(() => {
    navigate({
      pathname: location.pathname,
      search: location.search,
      hash: ''
    }, {
      replace: true
    });
  }, [navigate, location.pathname, location.search]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);
  return createPortal(<AnimatePresence data-id="element-532" data-mp="ek7k0zmgvu4m">
      {open && <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-6" data-id="element-533" data-mp="3vu5qlvo3yd05">
          <motion.div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} onClick={close} aria-hidden data-id="element-534" data-mp="4ncrqygsepys" />
          <motion.section role="dialog" aria-modal="true" aria-labelledby="scoring-title" initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30,
        scale: 0.97
      }} transition={{
        duration: 0.28,
        ease
      }} className="relative w-full max-w-lg" data-id="element-535" data-mp="1l1l1fsggl4t0">
            {/* Flotación sutil del panel */}
            <motion.div animate={reduce ? undefined : {
          y: [0, -4, 0]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="relative max-h-[92vh] overflow-y-auto rounded-t-3xl border border-neon/60 bg-surface/95 shadow-[0_0_0_1px_rgba(77,140,255,0.25),0_30px_80px_-20px_rgba(20,99,255,0.7)] backdrop-blur-xl sm:rounded-3xl" data-id="element-536" data-mp="19navk0bwyon7">
              <span aria-hidden className="absolute inset-x-10 top-0 h-px bg-neon-hi shadow-[0_0_12px_#4D8CFF]" data-id="element-537" data-mp="1skc7y67srr16" />
              <header className="relative flex items-start gap-4 px-6 pb-4 pt-6" data-id="element-538" data-mp="2q2c7bhzln4t8">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-neon/60 bg-neon/15 text-neon-hi shadow-[0_0_20px_rgba(20,99,255,0.45)]" data-id="element-539" data-mp="2l8zv67c3wqhd">
                  <GaugeIcon className="h-6 w-6" aria-hidden data-id="element-540" data-mp="2xjqu7sh6r3c3" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-541" data-mp="32jdbcd04x3q6">
                  <h2 id="scoring-title" className="text-metal font-display text-2xl font-black tracking-tight" data-id="element-542" data-mp="32ik6jhkqkoao">SCORING RÁPIDO</h2>
                  <p className="mt-0.5 text-sm text-mist" data-id="element-543" data-mp="37g3nip8tjf6b">Tu situación crediticia en segundos.</p>
                </div>
                <button type="button" onClick={close} className="rounded-full p-2 text-mist transition-colors duration-150 hover:bg-white/10 hover:text-white" aria-label="Cerrar" data-id="element-544" data-mp="3giv2gz0wkt2p">
                  <XIcon className="h-5 w-5" data-id="element-545" data-mp="3lc7du7gkn3di" />
                </button>
              </header>
              <div className="flex flex-wrap gap-2 px-6" data-id="element-546" data-mp="3qb0prvg7mmbk">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-ink/60 px-3 py-1 text-xs text-silver-hi" data-id="element-547" data-mp="3v92aektaatp5">
                  <ZapIcon className="h-3.5 w-3.5 text-neon-hi" aria-hidden data-id="element-548" data-mp="31vq4dhm7e7w" /> Resultado al instante
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-ink/60 px-3 py-1 text-xs text-silver-hi" data-id="element-549" data-mp="vu9uu7gcd63q">
                  <ShieldCheckIcon className="h-3.5 w-3.5 text-ok" aria-hidden data-id="element-550" data-mp="19kwvw69kr82z" /> Datos protegidos
                </span>
              </div>
              <div className="px-6 pb-6 pt-5" data-id="element-551" data-mp="hu8waynjuarf">
                <QuickScoringForm data-id="element-552" data-mp="hvaavd3rxfdw" />
              </div>
            </motion.div>
          </motion.section>
        </div>}
    </AnimatePresence>, document.body);
}